// Per rule: code-comments-required (ai.knowledge/global) — explain why.
// Per rule: reference-rules-in-comments (ai.knowledge/global) — name the rules.
//
// Pure helpers used by billing.tsx to classify a backend mailroom
// response into "success" vs "show toast" outcomes. ENG-3782 surfaces
// backend errors that previously were silently swallowed: backend
// returns either a thrown rejection (network/timeout) or a 200 with
// resp.error populated and resp.redirectUrl/url empty (Lambda recovered
// from a stale Stripe subscription with mailroomUtils.NewAPIError(500)).
// Both must produce a visible toast and KEEP the dialog open.
//
// Extracted as pure helpers so the decision logic can be unit-tested
// without React + depot scaffolding. Per the plan's H-TESTS-NEED-SEAMS
// finding: "pure helpers + manual QA over integration tests with seams".
//
// The two response shapes share the protobuf Error type; we accept a
// minimal structural type so this helper does not import generated
// protobuf types (avoids test setup pulling in protobuf chain).

/**
 * Minimal structural shape for a mailroom protobuf-shaped response.
 * Both BillingRegistryChangeRoomPlanResponse and
 * BillingRegistryGetRoomBillingPortalURLResponse satisfy this — they
 * each have an optional `error` and a single redirect/url string.
 *
 * Per CR7 (post-fix Codex review): `error.code` is included alongside
 * `error.message` because the protobuf Error type carries BOTH fields.
 * A populated error.code with empty message is a real failure mode we
 * must surface — the previous version only checked message and would
 * silently fall through to the success branch on `{ code: 5,
 * message: '' }`.
 */
export type BillingResponseLike = {
  error?: { code?: number; message?: string } | undefined;
  // Either redirectUrl (changeRoomPlan) or url (getBillingPortal)
  redirectUrl?: string;
  url?: string;
};

export type BillingToastDecision =
  | { kind: 'success'; redirectUrl: string }
  | { kind: 'error'; message: string };

/**
 * classifyBillingResponse decides whether to redirect the user to the
 * Stripe-hosted page (success) or surface a toast (error).
 *
 * The two failure modes that previously closed the dialog silently:
 *   1. resp.error?.message populated by the Lambda recovery path.
 *   2. resp.url / resp.redirectUrl empty even though resp.error is
 *      undefined (defensive — should not happen but covers the case
 *      where the Lambda returned a malformed success response).
 *
 * `genericFallback` is the user-visible message used when the backend
 * did not provide one. Different call sites use different copy
 * ("Could not start upgrade" vs "Could not open billing portal") so
 * the caller passes the right text.
 */
export const classifyBillingResponse = (
  resp: BillingResponseLike,
  genericFallback: string
): BillingToastDecision => {
  // CR7 from the post-fix Codex review: an error is "populated" when
  // EITHER error.code is set to a non-zero value OR error.message is
  // a non-empty string. Previous version only checked message; a
  // protobuf-shaped error like `{ code: 5, message: '' }` (proto3
  // default for unset string is '') silently fell through to the
  // success branch if a URL was also present.
  //
  // Per user-global rule (CLAUDE.md): no implicit truthiness — code
  // and message are both checked explicitly against undefined / 0 / ''.
  const errCode = resp.error?.code;
  const serverMessage = resp.error?.message;
  const hasErrorCode = errCode !== undefined && errCode !== 0;
  const hasErrorMessage = serverMessage !== undefined && serverMessage !== '';

  if (hasErrorCode || hasErrorMessage) {
    // Prefer the server message when it carries text; fall back to
    // the generic copy when only a code was set (we don't have a
    // user-friendly message-by-code map, and the code alone isn't
    // useful to a user).
    const trimmed = serverMessage?.trim() ?? '';
    return {
      kind: 'error',
      message: trimmed !== '' ? trimmed : genericFallback
    };
  }

  // Both response shapes have a single URL field. Handle both.
  // Trim before the empty check so a whitespace-only URL counts as
  // empty (defensive against malformed Lambda responses).
  const targetUrl = (resp.redirectUrl ?? resp.url ?? '').trim();
  if (targetUrl === '') {
    return { kind: 'error', message: genericFallback };
  }

  return { kind: 'success', redirectUrl: targetUrl };
};

/**
 * thrownRequestMessage returns the toast message used when the
 * mailroom request itself rejects (network failure, timeout, NATS
 * disconnect, etc.). Pure helper for symmetry with
 * classifyBillingResponse so the test suite covers both branches.
 */
export const thrownRequestMessage = (): string =>
  'We could not reach the billing service. Please try again in a moment.';
